import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { routePaths } from '@/app/routes';
import { useActivePatient } from '@/features/patients/hooks/useActivePatient';
import { useResolver } from '@/features/nomenclature/hooks/useResolver';
import { useAppointments } from '@/features/appointments/hooks/useAppointments';
import { useResults } from '@/features/results/hooks/useResults';
import { AppointmentCard } from '@/features/appointments/components/AppointmentCard';

interface StatCardProps {
  to: string;
  label: string;
  value: number | string;
  hint: string;
  loading?: boolean;
}

function StatCard({ to, label, value, hint, loading }: StatCardProps) {
  return (
    <Link to={to} className="group">
      <Card className="flex h-full flex-col justify-between p-5 transition-shadow group-hover:shadow-lg">
        <p className="text-sm font-medium text-ink-mute">{label}</p>
        <p className="mt-2 text-3xl font-extrabold text-primary">
          {loading ? '—' : value}
        </p>
        <p className="mt-1 text-sm text-ink-mute">{hint}</p>
      </Card>
    </Link>
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const { patient } = useActivePatient();
  const fhirId = patient?.fhirId ?? null;

  const appointments = useAppointments(fhirId);
  const results = useResults(fhirId);
  const { data: resolver = null } = useResolver();

  const nextAppointment = appointments.upcoming[0];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('dashboard.greeting', { name: patient?.firstName ?? '' })}
        subtitle={t('dashboard.subtitle')}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <StatCard
          to={routePaths.appointments}
          label={t('nav.appointments')}
          value={appointments.upcoming.length}
          hint={t('dashboard.upcoming_hint')}
          loading={appointments.isLoading}
        />
        <StatCard
          to={routePaths.results}
          label={t('nav.results')}
          value={results.data?.length ?? 0}
          hint={t('dashboard.results_hint')}
          loading={results.isLoading}
        />
      </div>

      {nextAppointment && (
        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-mute">
            {t('dashboard.next_appointment')}
          </h2>
          <AppointmentCard appointment={nextAppointment} variant="upcoming" resolver={resolver} />
        </section>
      )}
    </div>
  );
}
